import { useDispatch, useSelector } from "react-redux";
import {
  getConsolidatedInvoices,
  setConsolidatedInvoiceDetailsPopupData,
  setGridCurrentPageConsolidatedInvoice,
  setNumberOfRowsPerPage,
  setSelectedItems,
  setConsolidatedFilterDataField,
} from "src/redux/slices/invoiceSlice";
import { useTranslation } from "react-i18next";
import { DataTable, DataTableSortEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { useEffect, useRef, useState } from "react";

import { Button } from "primereact/button";
import HorizontalDivider from "src/components/HorizontalDivider/HorizontalDivider";
import { RoutePaths } from "src/resources/enum";
import ConsolidatedInvoicesFilter from "./ConsolidatedInvoicesFilter";
import { useNavigate } from "react-router";
import { getTextValueById } from "src/utils/helpers";
import GridPaginator from "src/components/GridPaginator/GridPaginator";
import { isConsolidatedInvoiceNotPaid } from "src/utils/common";
import { setChangeHistoricalDataSource } from "src/redux/slices/sharedSlice";
import { SortOrderDefault } from "src/resources/constant";

const ConsolidatedInvoicesGrid = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [sortOrder, setSortOrder] = useState<any | null>(SortOrderDefault);

  const { consolidatedInvoiceData } = useSelector(
    (state: any) => state.invoices
  );
  const { t } = useTranslation(["common", "invoice"]);

  const currentPage = useSelector(
    (state: any) => state.invoices.gridCurrentPageConsolidatedInvoice
  );
  const { showHistoricalData, sortColumn } = useSelector(
    (state: any) => state.invoices.consolidatedFilterData
  );
  const { showHistoricalDataChangeSource } = useSelector(
    (state: any) => state.shared
  );

  const { totalConsolidatedRowsCount, numberOfRowsPerPage } = useSelector(
    (state: any) => state.invoices
  );
  const { invoiceStatusOptions, consolidatedInvoiceStatusOptions } =
    useSelector((state: any) => state.lookup);

  const tableRef = useRef<DataTable<(typeof consolidatedInvoiceData)[0]>>(null);

  const invoiceIdTemplate = (rowData: any) => {
    return (
      <div onClick={() => handleInvoiceIdClick(rowData)}>
        {rowData.consolidatedInvoiceNumber}
      </div>
    );
  };

  const payButtonTemplate = (rowData: any) => {
    if (isConsolidatedInvoiceNotPaid(rowData.status)) {
      return (
        <Button
          label={t("payInvoice")}
          onClick={() => handleInvoiceIdClick(rowData)}
          className=" d-flex pay-invoice-button m-0 justify-content-center"
        ></Button>
      );
    } else {
      return <div></div>;
    }
  };

  const handleInvoiceIdClick = (rowData: any) => {
    const selectedInvoice = consolidatedInvoiceData.find(
      (invoice: any) =>
        invoice.consolidatedInvoiceNumber === rowData.consolidatedInvoiceNumber
    );
    dispatch(setConsolidatedInvoiceDetailsPopupData(selectedInvoice));
    const mappedData = rowData.invoiceList.map((item: any) => {
      return {
        ...item,
        status: getTextValueById(
          item.invoiceStatus.value,
          invoiceStatusOptions,
          "id"
        ),
      };
    });
    dispatch(setSelectedItems(mappedData));
    navigate(
      `${RoutePaths.ViewConsolidatedInvoiceDetails}?consolidatedInvoiceId=${rowData?.consolidatedInvoiceId}`
    );
  };

  useEffect(() => {
    dispatch(getConsolidatedInvoices() as any);
  }, []);

  const paymentStatusTemplate = (rowData: any) => {
    return getTextValueById(
      rowData.status,
      consolidatedInvoiceStatusOptions,
      "id"
    );
  };

  const onSort = (e: DataTableSortEvent) => {
    let sortFieldBEName = null;
    setSortOrder(e.sortOrder);
    if (e.sortField === "status") sortFieldBEName = "invoiceStatus";
    dispatch(
      setConsolidatedFilterDataField({
        field: "sortColumn",
        value: sortFieldBEName ?? e.sortField,
      })
    );
    dispatch(
      setConsolidatedFilterDataField({
        field: "isAscending",
        value: e.sortOrder === 1,
      })
    );
    dispatch(setGridCurrentPageConsolidatedInvoice(0));
  };

  const handleFormFieldChange = (e: any) => {
    const name = e.target.name;
    const value = e.target.checked;
    dispatch(setConsolidatedFilterDataField({ field: name, value }));
  };

  useEffect(() => {
    dispatch(setGridCurrentPageConsolidatedInvoice(0));
  }, [dispatch]);

  useEffect(() => {
    if (showHistoricalDataChangeSource === "") return;
    const index = parseInt(showHistoricalDataChangeSource.toString());
    if (index >= 10)
      //the number that we set in the ShowHistoricalData component
      dispatch(setGridCurrentPageConsolidatedInvoice(0));
    dispatch(setChangeHistoricalDataSource(""));
  }, [showHistoricalDataChangeSource]);

  return (
    <>
      <ConsolidatedInvoicesFilter />
      <HorizontalDivider />
      

      <div className="grid-outer-wrapper">
        <div className="grid-inner-wrapper">
          <DataTable
            ref={tableRef}
            lazy
            className="maintenance-requests-grid view-invoice-grid"
            value={consolidatedInvoiceData}
            emptyMessage={
              showHistoricalData ? t("emptyConsolidatedInvoicesRecords") : t("emptyUnpaidRecords")
            }
            dataKey="consolidatedInvoiceId"
            sortField={sortColumn || undefined}
            sortOrder={sortOrder || null} // Ensure sortOrder is a number or null
            onSort={onSort}
            scrollable
          >
            <Column
              field="consolidatedInvoiceNumber"
              header={t("invoiceNo")}
              body={invoiceIdTemplate}
              className="case-id-col md-col md-lg-col"
              sortable
            ></Column>
            <Column
              field="status"
              header={t("invoiceStatus")}
              body={paymentStatusTemplate}
              className="md-col"
              sortable
            ></Column>
            <Column
              field="totalAmount"
              header={t("amount")}
              className="sm-col"
            ></Column>
            {<Column body={payButtonTemplate} className="sm-col"></Column>}
          </DataTable>
        </div>
        <GridPaginator
          totalRowsCount={totalConsolidatedRowsCount}
          currentPage={currentPage}
          numberOfRowsPerPage={numberOfRowsPerPage}
          setNumberOfRowsPerPage={setNumberOfRowsPerPage}
          setGridCurrentPage={setGridCurrentPageConsolidatedInvoice}
          tableRef={tableRef}
        />
      </div>
    </>
  );
};

export default ConsolidatedInvoicesGrid;
